'use client'

import { useState } from 'react'
import { FaleConoscoService } from '../../lib/services'
import { formatTelefone, cleanTelefone } from '../../lib/utils/formatters'

export default function FaleConoscoPage() {
  const [formData, setFormData] = useState({
    nomeEmpresa: '',
    emailEmpresa: '',
    telefoneEmpresa: '',
    assunto: '',
    comentario: ''
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error'>('success')

  const assuntos = [
    { id: '1', assunto: 'Dúvidas sobre agendamento' },
    { id: '2', assunto: 'Problemas técnicos' },
    { id: '3', assunto: 'Cancelamento de exames' },
    { id: '4', assunto: 'Reembolso' },
    { id: '5', assunto: 'Outros' }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    let formattedValue = value

    // Aplicar máscara de telefone
    if (name === 'telefoneEmpresa') {
      formattedValue = formatTelefone(value)
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      // Preparar dados para a API
      const contatoData = {
        assunto_id: parseInt(formData.assunto),
        enviado_para: 'suporte@sstexames.com.br', // Email padrão
        nome: formData.nomeEmpresa,
        email: formData.emailEmpresa,
        telefone: cleanTelefone(formData.telefoneEmpresa), // Limpar formatação antes de enviar
        comentario: formData.comentario,
      }

      // Enviar contato via API
      await FaleConoscoService.enviarContato(contatoData)

      setMessageType('success')
      setMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.')

      // Reset form
      setFormData({
        nomeEmpresa: '',
        emailEmpresa: '',
        telefoneEmpresa: '',
        assunto: '',
        comentario: ''
      })
    } catch (error: any) {
      console.error('Erro ao enviar contato:', error)
      setMessageType('error')

      // Melhor tratamento de erro
      let errorMessage = 'Erro ao enviar mensagem. Tente novamente.'

      if (error?.message) {
        errorMessage = error.message
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      // Formatar mensagem
      errorMessage = String(errorMessage || 'Erro desconhecido')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/\s+/g, ' ')
        .trim()

      setMessage(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Fale Conosco
            </h1>
            <p className="text-lg text-gray-600">
              Estamos aqui para ajudar! Entre em contato conosco para tirar suas dúvidas ou resolver qualquer problema.
            </p>
          </div>


          {/* Formulário */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Envie sua mensagem
            </h2>

            {message && (
              <div className={`mb-6 p-4 rounded-lg border ${messageType === 'success'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
                }`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {messageType === 'success' ? (
                      <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 011.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`text-sm font-medium ${messageType === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}>
                      {messageType === 'success' ? 'Mensagem enviada!' : 'Erro no envio'}
                    </p>
                    <p className={`mt-1 text-sm ${messageType === 'success' ? 'text-green-700' : 'text-red-700'
                      }`}>
                      {message}
                    </p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setMessage('')}
                      className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${messageType === 'success'
                        ? 'text-green-500 hover:bg-green-100 focus:ring-green-600'
                        : 'text-red-500 hover:bg-red-100 focus:ring-red-600'
                        }`}
                    >
                      <span className="sr-only">Fechar</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    name="nomeEmpresa"
                    value={formData.nomeEmpresa}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    placeholder="Seu nome completo"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="emailEmpresa"
                    value={formData.emailEmpresa}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="telefoneEmpresa"
                    value={formData.telefoneEmpresa}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assunto *
                  </label>
                  <select
                    name="assunto"
                    value={formData.assunto}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    required
                  >
                    <option value="">Selecione um assunto</option>
                    {assuntos.map(assunto => (
                      <option key={assunto.id} value={assunto.id}>
                        {assunto.assunto}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mensagem *
                </label>
                <textarea
                  name="comentario"
                  value={formData.comentario}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                  placeholder="Digite sua mensagem aqui..."
                  required
                />
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {loading ? 'Enviando...' : 'Enviar Mensagem'}
                </button>
              </div>
            </form>
          </div>

          {/* FAQ Link CTA */}
          <div className="text-center mt-12">
            <div className="bg-green-50 border border-green-200 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-green-900 mb-4">
                Dúvidas Frequentes
              </h3>
              <p className="text-green-700 mb-6">
                Antes de entrar em contato, verifique se sua dúvida já foi respondida em nossa seção de perguntas frequentes.
              </p>
              <a
                href="/faq"
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Ver FAQ
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

